<template>
  <div>
    <div class="card">
      <div class="card-header d-flex justify-content-between">
        <div class="mt-1">
          <input
            type="file"
            id="file"
            ref="file"
            v-on:change="handleFileChange($event)"
            multiple
            hidden
          />
          <i class="fad fa-info-circle text-primary"></i>
          Glisser-déposez vos images ci-dessous ou
          <a href="#" @click.prevent="selectFiles()">
            cliquez ici pour sélectionner les fichiers à télécharger</a
          >.
        </div>
        <div>
          <button
            v-if="showImportButton"
            class="btn btn-primary btn-sm"
            @click.prevent="importMediaFromTrim"
          >
            <i class="far fa-file-import"></i>
            Importer les images depuis une finition
          </button>
          <a href="#" v-if="photos.length > 0" @click="removeAll" class="btn btn-danger btn-sm ml-1"
            ><i class="far fa-trash"></i>&nbsp;Supprimer tous les fichiers</a
          >
        </div>
      </div>
      <div class="gallery-container" @drop.prevent="handleFileDrop($event)" @dragover.prevent>
        <div v-show="photos.length === 0" class="text-center my-3">
          <h1>Déposez vos fichiers ici</h1>
          <i class="fad fa-cloud-upload-alt fa-10x text-secondary"></i>
        </div>
        <draggable
          :list="photos"
          v-bind="dragOptions"
          item-key="file"
          class="gallery"
          @start="drag = true"
          @end="drag = false"
          @change="savePosition(photos)"
        >
          <template #item="{ element }">
            <figure :key="element.file">
              <input
                type="checkbox"
                class="form-control checkbox-media"
                v-model="selectedPhotos"
                :value="element"
              />
              <img :src="element.thumb" loading="lazy" @click="toggleMediaSelection(element)" />
              <figcaption>{{ element.name }}</figcaption>
              <div class="metadata">
                <i v-show="isMediaSelected(element)" class="fad fa-check-square"></i>
                <span class="filesize">{{ Math.floor(element.size / 1024) }} kb</span>
                <a
                  href="#"
                  @click.prevent="removeFile(element, false)"
                  title="Remove"
                  class="text-danger"
                >
                  <i class="fad fa-trash"></i>
                </a>
              </div>
            </figure>
          </template>
        </draggable>
      </div>
    </div>
    <div>
      <!-- Modal-->
      <transition
        @enter="startTransitionModal"
        @after-enter="endTransitionModal"
        @before-leave="endTransitionModal"
        @after-leave="startTransitionModal"
      >
        <div class="modal fade" v-show="showModal" ref="modal">
          <div class="modal-dialog modal-dialog-centered" role="document">
            <div class="modal-content">
              <div class="modal-header bg-primary">
                <h5 class="modal-title" id="exampleModalLabel">Transfert des fichiers en cours</h5>
                <button class="close" type="button" @click="closeFileTransferWindow()">
                  <span aria-hidden="true">×</span>
                </button>
              </div>
              <div class="modal-body">
                <div class="text-center">
                  {{ files.filter(x => x.uploaded).length }} / {{ files.length }} fichiers
                  transférés
                </div>
                <div class="progress mb-3">
                  <div
                    class="progress-bar bg-success"
                    role="progressbar"
                    :aria-valuenow="uploadProgress"
                    aria-valuemin="0"
                    aria-valuemax="100"
                    :style="'width: ' + uploadProgress + '%'"
                  >
                    <span class="sr-only">{{ uploadProgress }}% Complete (success)</span>
                  </div>
                </div>
                <a
                  class="collapsed"
                  data-toggle="collapse"
                  href="#uploadDetails"
                  role="button"
                  aria-expanded="false"
                  aria-controls="uploadDetails"
                >
                  Afficher les détails
                </a>
                <ul class="collapse" id="uploadDetails">
                  <li v-for="entry in files" :key="entry.file.name" class="py-1">
                    {{ entry.file.name }} ({{ Math.floor(entry.file.size / 1024) }}
                    kb)
                    <small
                      v-if="entry.error"
                      :class="entry.error == 500 ? 'badge-danger' : 'badge-warning'"
                      class="badge"
                      >{{ getErrorMessage(entry.error) }}</small
                    >
                    <span
                      v-else
                      :class="entry.uploaded ? 'badge-success' : 'badge-light'"
                      class="badge"
                      >{{ entry.uploaded ? 'Chargé' : 'En cours' }}</span
                    >
                  </li>
                </ul>
              </div>
              <div class="modal-footer">
                <button type="button" class="btn btn-primary" @click="closeFileTransferWindow()">
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      </transition>
      <div class="modal-backdrop fade d-none" ref="backdrop"></div>
      <batch-actions
        :files="selectedPhotos"
        @closeBatchBox="closeBatchBox"
        @removeItems="removeItems"
      ></batch-actions>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.checkbox-media {
  position: absolute;
  left: -45%;
  z-index: 999;
}

.card-header::after {
  content: inherit;
}

.gallery-container {
  width: 100%;
  padding: 1em 2%;

  .gallery {
    margin-bottom: 5%;
    --THUMBNAILS_WIDTH: 250px;
    --THUMBNAILS_HEIGHT: 250px;

    @media (max-width: 400px) {
      --THUMBNAILS_WIDTH: 200px;
      --THUMBNAILS_HEIGHT: 2 à0px;
    }

    display: grid;
    grid-template-columns: repeat(auto-fit, var(--THUMBNAILS_WIDTH));
    grid-template-rows: auto;
    column-gap: clamp(0.1em, 3%, 1vw);
    row-gap: clamp(0.1em, 3%, 3vh);
    justify-items: center;
    align-items: center;

    figure {
      height: var(--THUMBNAILS_HEIGHT);
      width: var(--THUMBNAILS_WIDTH);
      position: relative;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;

        &:hover {
          cursor: move;
        }
      }

      figcaption {
        position: absolute;
        bottom: 0;
        font-size: 12px;
        color: #fff;
        padding: 2% 3%;
        background: rgba(0, 0, 0, 0.6);
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 1;
        overflow: hidden;
        min-width: 100%;
      }

      .metadata {
        position: absolute;
        right: 0;
        bottom: -1.5rem;
        font-size: 0.9rem;
      }
    }
  }
}

.modal {
  small.badge {
    font-size: 80% !important;
  }
}
</style>

<script>
import draggable from 'vuedraggable'
import axios from 'axios'
import BatchActions from '@components/batch-section/batch-actions.vue'
import { ref, reactive, computed, onMounted, inject } from 'vue'

export default {
  components: {
    draggable,
    BatchActions
  },
  props: {
    uploadUrl: {
      type: String,
      default: ''
    },
    mediaUrl: {
      type: String,
      default: ''
    },
    galleryId: {
      type: Number,
      default: 0
    },
    showImportButton: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const store = inject('store')

    const selectedPhotos = ref([])
    const showModal = ref(false)
    const uploadProgress = ref(0)
    const selectedFiles = ref([])
    const files = ref([])
    const initialLoading = ref(true)

    const backdrop = ref(null)
    const modal = ref(null)

    const dragOptions = [
      {
        forceFallback: true,
        animation: 200,
        ghostClass: 'ghost'
      }
    ]

    const statusCodeMessages = new Map([
      [409, 'Fichier déjà chargé.'],
      [500, 'Impossible de charger ce fichier.'],
      [403, 'Chargement non autorisé.']
    ])

    onMounted(() => {
      initialLoading.value = true

      return store
        .dispatch('gallery/loadMedia', props.mediaUrl)
        .then(() => (initialLoading.value = false))
    })

    const photos = computed({
      get() {
        return store.state.gallery.media
      },
      set(value) {
        store.dispatch('gallery/updateMediaList', {
          url: props.mediaUrl,
          list: value
        })
      }
    })

    const headers = computed(() => {
      let headers = {
        'Content-Type': 'application/json;charset=utf-8'
      }
      let token = document.head.querySelector('meta[name="csrf-token"]')

      if (token !== '') {
        headers['X-CSRF-TOKEN'] = token.content
      }
      return { headers }
    })

    const uploadedFilesCount = computed(() => {
      return files.value.filter(x => {
        return x.uploaded
      }).length
    })

    const startTransitionModal = () => {
      backdrop.value.classList.toggle('d-block')
      if (modal.value) {
        modal.value.classList.toggle('d-block')
      }
    }

    const endTransitionModal = () => {
      backdrop.value.classList.toggle('show')
      modal.value.classList.toggle('show')
    }

    return {
      backdrop,
      modal,
      selectedPhotos,
      showModal,
      uploadProgress,
      selectedFiles,
      files,
      initialLoading,
      dragOptions,
      statusCodeMessages,
      photos,
      headers,
      uploadedFilesCount,
      startTransitionModal,
      endTransitionModal
    }
  },
  methods: {
    closeBatchBox() {
      this.selectedPhotos = []
    },
    removeItems() {
      this.$swal({
        title: 'Vous êtes sur?',
        text: 'Une fois supprimé, vous ne pourrez pas récupérer ces fichiers!',
        icon: 'warning',
        buttons: true,
        showCancelButton: true,
        confirmButtonText: 'Supprimer',
        cancelButtonText: 'Annuler',
        showCloseButton: true,
        showLoaderOnConfirm: true
      }).then(res => {
        if (res.isConfirmed) {
          this.selectedPhotos.forEach(x => this.removeFile(x, true))
          this.$swal({
            text: 'Photos supprimées',
            icon: 'success'
          })
          this.selectedPhotos = []
        } else {
          this.$swal({
            text: 'Opération annulée',
            icon: 'error'
          })
        }
      })
    },
    removeAll() {
      this.$swal({
        title: 'Êtes-vous sûr?',
        text: 'Une fois supprimé, vous ne pourrez pas récupérer ces fichiers!',
        icon: 'warning',
        buttons: true,
        showCancelButton: true,
        confirmButtonText: 'Supprimer',
        cancelButtonText: 'Annuler',
        showCloseButton: true,
        showLoaderOnConfirm: true
      }).then(res => {
        if (res.isConfirmed) {
          this.photos.forEach(x => this.removeFile(x))
          this.$swal({
            text: 'Photos supprimées',
            icon: 'success'
          })
          this.selectedPhotos = []
        } else {
          this.$swal({
            text: 'Opération annulée',
            icon: 'error'
          })
        }
      })
    },
    savePosition(value) {
      axios
        .put(this.mediaUrl + '/order', JSON.stringify(value.map(x => x.id)), this.headers)
        .then(() => {
          this.photos = value
        })
        .catch(e => {
          console.error(JSON.stringify(e.message))
        })
    },
    selectFiles() {
      this.$refs.file.click()
    },
    toggleMediaSelection(file) {
      return // feature is disabled until batch actions are available
      // commentend code to avoid console warning
      //   if (this.isMediaSelected(file)) {
      //     this.selectedFiles.splice(this.selectedFiles.indexOf(file), 1);
      //   } else {
      //     this.selectedFiles.push(file);
      //   }
    },
    isMediaSelected(file) {
      return this.selectedFiles.indexOf(file) > -1
    },
    handleFileDrop(event) {
      let droppedFiles = event.dataTransfer.files
      if (!droppedFiles || droppedFiles.length === 0) {
        return
      }

      this.addFiles(droppedFiles)
    },
    handleFileChange(event) {
      if (!event.target.files || event.target.files.length === 0) {
        return
      }

      this.addFiles(event.target.files)
    },
    closeFileTransferWindow() {
      this.files = this.files.filter(x => !x.uploaded)
      this.showModal = false
    },
    addFiles(files) {
      this.showModal = true
      this.uploadProgress = 0
      ;[...files].forEach(f => {
        let file = {
          file: f,
          uploaded: false,
          error: null
        }

        this.files.push(file)
        let formData = new FormData()
        formData.append('file', f)

        let headers = {
          'Content-Type': 'multipart/form-data'
        }
        let token = document.head.querySelector('meta[name="csrf-token"]')

        if (token !== '') {
          headers['X-CSRF-TOKEN'] = token.content
        }

        axios
          .post(this.uploadUrl, formData, headers)
          .then(() => {
            file.uploaded = true

            this.$store
              .dispatch('gallery/loadMedia', this.mediaUrl)
              .then(() => (this.initialLoading = false))
            this.selectedPhotos = []
          })
          .catch(e => {
            let statusCode = parseInt(e.message.split('code ').pop())
            file.error = isNaN(statusCode) ? 500 : statusCode
            console.error(JSON.stringify(e.message))
          })
          .finally(() => {
            this.updateUploadProgress()
          })
      })
    },
    removeFile(file, deleteMethod = true) {
      if (file) {
        this.$store.dispatch('gallery/deleteMedia', {
          id: file.id,
          url: this.mediaUrl + '/' + file.id
        })
        if (!deleteMethod) {
          this.selectedPhotos.indexOf(file) > -1
            ? this.selectedPhotos.splice(this.selectedPhotos.indexOf(file), 1)
            : ''
        }
      } else {
        this.$store.dispatch('gallery/removeMediaFromStore', file)
      }
    },
    getErrorMessage(statusCode) {
      return this.statusCodeMessages.has(statusCode)
        ? this.statusCodeMessages.get(statusCode)
        : this.statusCodeMessages.get(500)
    },
    importMediaFromTrim() {
      this.$store.dispatch('gallery/attachMediaFromTrim', this.mediaUrl).then(() => {
        this.$store.dispatch('gallery/loadMedia', this.mediaUrl)
      })
    },
    updateUploadProgress() {
      let uploaded = 0
      this.files.forEach(x => {
        if (x.uploaded) {
          uploaded++
        }
      })

      this.uploadProgress = (uploaded / this.files.length) * 100
    }
  }
}
</script>
